(() => {
  'use strict';

  const qs = (root, selector) => root?.querySelector(selector) || null;
  const qsa = (root, selector) => [...(root?.querySelectorAll(selector) || [])];
  const normalizeLabel = (value) => String(value || '').trim().toLocaleLowerCase('nl');
  const escapeHtml = (value) => String(value || '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  const init = () => {
    const host = document.querySelector('[data-bm-vehicle-dialog]');
    if (!host || host.dataset.bmVehicleDialogReady === 'true') return;
    host.dataset.bmVehicleDialogReady = 'true';

    const panel = qs(host, '.bm-vehicle-dialog__panel');
    const backdrop = qs(host, '.bm-vehicle-dialog__backdrop');
    const stages = qsa(host, '[data-bm-dialog-stage]');
    const back = qs(host, '[data-bm-dialog-back]');
    const step = qs(host, '[data-bm-dialog-step]');
    const title = qs(host, '[data-bm-dialog-title]');
    const searchWrap = qs(host, '[data-bm-dialog-search-wrap]');
    const search = qs(host, '[data-bm-dialog-search]');
    const status = qs(host, '[data-bm-dialog-status]');
    const current = qs(host, '[data-bm-vehicle-dialog-current]');
    const currentLabel = qs(host, '[data-bm-vehicle-dialog-current-label]');
    const currentImage = qs(host, '[data-bm-vehicle-dialog-current-image]');
    const currentFallback = qs(host, '[data-bm-vehicle-dialog-current-fallback]');
    const progress = qsa(host, '[data-bm-dialog-progress]');
    const brandContainer = qs(host, '.bm-vehicle-dialog__brands');
    const brandAssets = new Map(qsa(host, '[data-bm-vehicle-brand-assets] [data-bm-brand-ref]')
      .map((node) => [node.dataset.bmBrandRef || '', node.dataset.bmBrandLogo || ''])
      .filter(([ref, logo]) => ref && logo));
    const modelContainer = qs(host, '[data-bm-dialog-options="model"]');
    const typeContainer = qs(host, '[data-bm-dialog-options="generation"]');

    const state = { brand: null, model: null, generation: null, stage: 'brand' };

    // Use only Bridge-verified native exact-vehicle page URLs.
    // Preview navigation remains in the same unpublished theme.
    const verifiedDestination = (raw) => {
      if (typeof raw !== 'string' || !raw.trim()) return '';
      try {
        const target = new URL(raw, window.location.origin);
        const shop = new URL(host.dataset.bmShopUrl);
        if (target.protocol !== 'https:'
          || shop.protocol !== 'https:'
          || (target.origin !== window.location.origin && target.origin !== shop.origin)
          || target.username || target.password
          || !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?pages\/[^/]+\/[^/]+\/?$/.test(target.pathname)
          || target.search || target.hash) return '';
        const current = new URL(window.location.href);
        const previewId = current.searchParams.get('preview_theme_id') || '';
        const previewHost = current.hostname === 'shopifypreview.com'
          || current.hostname.endsWith('.shopifypreview.com');
        if (!previewHost && !/^\d+$/.test(previewId)) return target.href;
        const next = new URL(target.pathname, current.origin);
        if (/^\d+$/.test(previewId)) next.searchParams.set('preview_theme_id', previewId);
        return next.href;
      } catch (_) { return ''; }
    };

    const selectionDestination = (vehiclePageUrl, returnUrl) => {
      if (returnUrl) return returnUrl;
      const hub = verifiedDestination(vehiclePageUrl);
      if (hub) return hub;
      // Never silently replace an invalid non-empty native Shopify URL.
      if (vehiclePageUrl) return '';
      // DRAFT exact pages keep their Vehicle UUID in Mijn Auto. The existing
      // NEW product catalog reads UUID-bound P12 results, not legacy vehicle hubs.
      try {
        const target = new URL(host.dataset.bmNewFallbackUrl || '', window.location.origin);
        if (target.origin !== window.location.origin
          || !/^(?:\/[a-z]{2}(?:-[a-z]{2})?)?\/collections\/nieuwe-velgen\/?$/.test(target.pathname)) return '';
        target.searchParams.set('browse', 'vehicle');
        const themeId = new URL(window.location.href).searchParams.get('preview_theme_id') || '';
        if (/^\d+$/.test(themeId)) target.searchParams.set('preview_theme_id', themeId);
        return target.href;
      } catch (_) { return ''; }
    };

    const isCurrentDestination = (destination) => {
      try {
        const url = new URL(destination, window.location.origin);
        return url.origin === window.location.origin
          && url.pathname === window.location.pathname
          && url.search === window.location.search;
      } catch (_) { return false; }
    };

    const cache = { brands: null, models: new Map(), types: new Map() };
    let opener = null;
    let previousVehicle = null;
    let selectionMade = false;
    let requestVersion = 0;

    const api = () => window.BandenmeestersVehicle;
    const client = () => window.BandenmeestersNewClient;
    const vehicleLabel = (vehicle) => api()?.vehicleLabel?.(vehicle)
      || [vehicle?.brand?.name, vehicle?.model?.name, vehicle?.newVehicle?.label].filter(Boolean).join(' ');

    const nodeData = (choice) => ({
      ref: choice?.dataset.ref || '',
      vehicleId: choice?.dataset.vehicleId || '',
      name: choice?.dataset.name || '',
      url: choice?.dataset.url || '',
      logo: choice?.dataset.logo || '',
      yearFrom: choice?.dataset.yearFrom || '',
      yearTo: choice?.dataset.yearTo || '',
    });

    const yearLabel = (item) => {
      if (!item?.yearFrom) return '';
      return `${item.yearFrom}–${item.yearTo || 'heden'}`;
    };

    const setCurrentImage = (vehicle, imageUrl = '') => {
      const hasImage = Boolean(imageUrl);
      if (currentImage) {
        if (hasImage) {
          currentImage.src = imageUrl;
          currentImage.alt = vehicleLabel(vehicle);
          currentImage.hidden = false;
        } else {
          currentImage.removeAttribute('src');
          currentImage.alt = '';
          currentImage.hidden = true;
        }
      }
      if (currentFallback) currentFallback.hidden = hasImage;
    };

    const renderCurrent = () => {
      const active = api()?.getActive?.() || previousVehicle;
      if (!current || !currentLabel) return;
      current.hidden = !active;
      if (!active) {
        setCurrentImage(null, '');
        return;
      }
      currentLabel.textContent = vehicleLabel(active);
      const image = active?.generation?.image || '';
      setCurrentImage(active, image);
    };

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const mobileViewport = window.matchMedia('(max-width: 749px)');
    let dialogVersion = 0, dialogAnimations = [];
    const cancelDialogMotion = () => {
      dialogAnimations.forEach(animation => animation.cancel());
      dialogAnimations = [];
    };
    // Same restrained sheet movement at every vehicle entry: no new visual owner.
    const dialogMotion = (opening) => {
      if (reducedMotion.matches || !panel?.animate) return [];
      const offset = window.matchMedia('(max-width: 749px)').matches ? 16 : 10;
      const frames = opening
        ? [{ opacity: .88, transform: `translateY(${offset}px)` }, { opacity: 1, transform: 'translateY(0)' }]
        : [{ opacity: 1, transform: 'translateY(0)' }, { opacity: .84, transform: `translateY(${offset}px)` }];
      const result = [panel.animate(frames, {
        duration: opening ? 210 : 170,
        easing: opening ? 'cubic-bezier(.22,.75,.22,1)' : 'ease-in',
        fill: 'both',
      })];
      if (backdrop?.animate) result.push(backdrop.animate(
        opening ? [{ opacity: 0 }, { opacity: 1 }] : [{ opacity: 1 }, { opacity: 0 }],
        { duration: opening ? 180 : 150, easing: opening ? 'ease-out' : 'ease-in', fill: 'both' }
      ));
      return result;
    };
    let stageVersion = 0, stageAnimation = null;
    const setStage = (name, animate = false) => {
      const previous = state.stage;
      const version = ++stageVersion;
      stageAnimation?.cancel();
      const body = qs(host, '[data-bm-dialog-body]');
      body?.removeAttribute('inert');
      state.stage = name;
      const commit = () => {
      const index = { brand: 0, model: 1, generation: 2 }[name] ?? 0;
      stages.forEach((stage) => {
        const active = stage.dataset.bmDialogStage === name;
        stage.hidden = !active;
        stage.classList.toggle('is-active', active);
      });
      progress.forEach((item, i) => {
        item.classList.toggle('is-active', i === index);
        item.classList.toggle('is-done', i < index);
      });
      if (back) back.hidden = name === 'brand';
       const resetButton = qs(host, '[data-bm-dialog-reset]');
       if (resetButton) resetButton.hidden = name === 'brand';
      if (searchWrap) searchWrap.hidden = false;
      if (search) {
        search.value = '';
        search.placeholder = name === 'brand'
          ? 'Zoek automerk...'
          : name === 'model'
            ? 'Zoek model...'
            : 'Zoek uitvoering of bouwjaar...';
      }
      if (step) step.textContent = `Stap ${index + 1} van 3`;
      if (title) {
        title.textContent = name === 'brand'
          ? 'Kies je merk'
          : name === 'model'
            ? `Kies je ${state.brand?.name || ''}-model`
            : 'Kies je uitvoering';
      }
      if (body) body.scrollTop = 0;
      // Keep focus on the current step, not the search field, on touch-sized screens.
      if (mobileViewport.matches) title?.focus({ preventScroll: true });
      };
      if (!animate || previous === name || !body?.animate || reducedMotion.matches) { commit(); return; }
      const order = ['brand', 'model', 'generation'];
      const direction = order.indexOf(name) > order.indexOf(previous) ? 1 : -1;
      body.setAttribute('inert', '');
      stageAnimation = body.animate([{ opacity: 1, transform: 'translateX(0)' }, { opacity: 0, transform: `translateX(${-direction * 12}px)` }],
        { duration: 90, easing: 'ease-in', fill: 'both' });
      stageAnimation.finished.then(() => {
        if (version !== stageVersion || host.hidden) return;
        stageAnimation.cancel(); body.removeAttribute('inert'); commit();
        if (!mobileViewport.matches) search?.focus({ preventScroll: true });
        stageAnimation = body.animate([{ opacity: 0, transform: `translateX(${direction * 12}px)` }, { opacity: 1, transform: 'translateX(0)' }],
          { duration: 160, easing: 'cubic-bezier(.22,.75,.22,1)', fill: 'both' });
        return stageAnimation.finished.then(() => { if (version === stageVersion) stageAnimation.cancel(); });
      }).catch(() => {});
    };
    reducedMotion.addEventListener?.('change', () => { if (reducedMotion.matches) setStage(state.stage); });

    const reset = () => {
      requestVersion++;
      state.brand = null;
      state.model = null;
      state.generation = null;
      if (modelContainer) modelContainer.innerHTML = '';
      if (typeContainer) typeContainer.innerHTML = '';
      if (search) search.value = '';
      setStage('brand');
      if (status) status.textContent = 'Kies eerst je automerk';
      renderBrands(cache.brands || []);
    };

    const createBrandChoice = (brand, index) => {
      const logo = brandAssets.get(brand.ref) || '';
      const choice = document.createElement('button');
      choice.type = 'button';
      choice.className = `bm-choice bm-vehicle-dialog__brand${logo ? '' : ' bm-vehicle-dialog__brand--text-only'}`;
      choice.dataset.choice = 'brand'; choice.dataset.ref = brand.ref; choice.dataset.name = brand.label; choice.dataset.logo = logo;
      choice.removeAttribute('data-id'); choice.removeAttribute('data-url');
      if (logo) {
        const visual = document.createElement('span'); visual.className = 'bm-vehicle-dialog__brand-logo'; visual.setAttribute('aria-hidden', 'true');
        const image = document.createElement('img'); image.src = logo; image.alt = ''; image.loading = index < 8 ? 'eager' : 'lazy'; image.decoding = 'async';
        visual.appendChild(image); choice.appendChild(visual);
      }
      const label = document.createElement('strong'); label.textContent = brand.label; choice.appendChild(label);
      const arrow = document.createElement('span'); arrow.className = 'bm-vehicle-dialog__arrow'; arrow.setAttribute('aria-hidden', 'true'); arrow.textContent = '›'; choice.appendChild(arrow);
      return choice;
    };

    const renderBrands = (brands) => {
      if (!brandContainer || !Array.isArray(brands) || !brands.length) return;
      const sorted = [...brands].sort((a, b) => a.label.localeCompare(b.label, 'nl', { numeric: true, sensitivity: 'base' }));
      brandContainer.replaceChildren(...sorted.map((brand, index) => createBrandChoice(brand, index)));
    };

    const createModelChoice = (model) => {
      const choice = document.createElement('button');
      choice.type = 'button';
      choice.className = 'bm-choice bm-choice--text-only';
      choice.dataset.choice = 'model';
      choice.dataset.ref = model.ref;
      choice.dataset.name = model.label;
      const label = document.createElement('span');
      label.className = 'bm-choice__name';
      label.textContent = model.label;
      choice.appendChild(label);
      const arrow = document.createElement('span');
      arrow.className = 'bm-vehicle-dialog__arrow';
      arrow.setAttribute('aria-hidden', 'true');
      arrow.textContent = '›';
      choice.appendChild(arrow);
      return choice;
    };

    const createTypeChoice = (type) => {
      const choice = document.createElement('button');
      choice.type = 'button';
      choice.className = 'bm-choice bm-choice--text-only';
      choice.dataset.choice = 'generation';
      choice.dataset.vehicleId = type.vehicleId;
      choice.dataset.name = type.label;
      choice.dataset.yearFrom = type.yearFrom ?? '';
      choice.dataset.yearTo = type.yearTo ?? '';
      choice.dataset.url = type.vehiclePageUrl || '';

      const wrapper = document.createElement('span');
      wrapper.className = 'bm-choice__generation';
      const copy = document.createElement('span');
      const years = yearLabel({ yearFrom: type.yearFrom, yearTo: type.yearTo });
      const name = document.createElement('span');
      name.className = 'bm-choice__generation-name';
      name.textContent = years || type.label;
      copy.appendChild(name);
      if (years && type.label) {
        const meta = document.createElement('small');
        meta.className = 'bm-choice__desktop-meta';
        meta.textContent = type.label;
        copy.appendChild(meta);
      }
      wrapper.appendChild(copy);
      choice.appendChild(wrapper);

      const arrow = document.createElement('span');
      arrow.className = 'bm-choice__arrow';
      arrow.setAttribute('aria-hidden', 'true');
      arrow.textContent = '›';
      choice.appendChild(arrow);
      return choice;
    };

    const renderError = (container, message, stage) => {
      if (!container) return;
      container.innerHTML = `
        <div class="bm-vehicle-dialog__empty bm-vehicle-dialog__retry">
          <span>${escapeHtml(message)}</span>
          <button type="button" data-bm-dialog-retry="${stage}">Opnieuw proberen</button>
        </div>`;
    };

    const ensureBrands = async ({ force = false } = {}) => {
      if (!client()?.vehicleBrands) throw new Error('Bridge client niet beschikbaar');
      if (!force && cache.brands) {
        renderBrands(cache.brands);
        return cache.brands;
      }
      if (brandContainer) brandContainer.innerHTML = '<div class="bm-vehicle-dialog__loading">Automerken laden...</div>';
      const payload = await client().vehicleBrands();
      cache.brands = payload.brands;
      renderBrands(payload.brands);
      return payload.brands;
    };

    const loadModels = async (brandRef, { force = false } = {}) => {
      if (!brandRef) return [];
      if (!force && cache.models.has(brandRef)) return cache.models.get(brandRef);
      const payload = await client().vehicleModels(brandRef);
      cache.models.set(brandRef, payload.models);
      return payload.models;
    };

    const loadTypes = async (brandRef, modelRef, { force = false } = {}) => {
      if (!brandRef || !modelRef) return [];
      const key = `${brandRef}:${modelRef}`;
      if (!force && cache.types.has(key)) return cache.types.get(key);
      const payload = await client().vehicleTypes(brandRef, modelRef);
      cache.types.set(key, payload.types);
      return payload.types;
    };

    const loadStage = async (stage, { force = false } = {}) => {
      const container = stage === 'model' ? modelContainer : typeContainer;
      if (!container) return false;
      const version = ++requestVersion;
      container.innerHTML = '<div class="bm-vehicle-dialog__loading">Laden...</div>';
      try {
        const rows = stage === 'model'
          ? await loadModels(state.brand?.ref, { force })
          : await loadTypes(state.brand?.ref, state.model?.ref, { force });
        if (version !== requestVersion) return false;
        container.replaceChildren(...rows.map(stage === 'model' ? createModelChoice : createTypeChoice));
        if (!rows.length) {
          container.innerHTML = `<div class="bm-vehicle-dialog__empty">Geen ${stage === 'model' ? 'modellen' : 'uitvoeringen'} gevonden.</div>`;
          return false;
        }
        return true;
      } catch (error) {
        if (version !== requestVersion) return false;
        console.error('Bandenmeesters voertuigassistent:', error);
        renderError(container, 'Kon de voertuiggegevens niet laden.', stage);
        return false;
      }
    };

    const closeMobileMenu = () => {
      const toggle = document.querySelector('[data-bm-mobile-menu-toggle][aria-expanded="true"]');
      if (toggle) toggle.click();
    };

    const open = async (trigger) => {
      const version = ++dialogVersion;
      cancelDialogMotion();
      host.removeAttribute('inert');
      opener = trigger || document.activeElement;
      previousVehicle = api()?.getActive?.() || null;
      selectionMade = false;
      closeMobileMenu();
      renderCurrent();
      host.hidden = false;
      document.body.classList.add('bm-vehicle-dialog-open');
      trigger?.setAttribute?.('aria-expanded', 'true');
      dialogAnimations = dialogMotion(true);
      dialogAnimations[0]?.finished.then(() => {
        if (version === dialogVersion) cancelDialogMotion();
      }).catch(() => {});
      requestAnimationFrame(() => qs(host, '.bm-vehicle-dialog__close')?.focus());
      reset();
      if (status) status.textContent = 'Automerken laden...';
      try {
        await ensureBrands();
        if (version !== dialogVersion) return;
        if (status) status.textContent = 'Kies eerst je automerk';
      } catch (error) {
        if (version !== dialogVersion) return;
        console.error('Bandenmeesters voertuigassistent:', error);
        renderError(brandContainer, 'Kon de automerken niet laden.', 'brand');
        if (status) status.textContent = 'Kon de automerken niet laden.';
      }
    };

    const close = () => {
      if (host.hidden || host.hasAttribute('inert')) return;
      const version = ++dialogVersion;
      cancelDialogMotion();
      stageVersion++; stageAnimation?.cancel();
      qs(host, '[data-bm-dialog-body]')?.removeAttribute('inert');
      host.setAttribute('inert', '');
      opener?.setAttribute?.('aria-expanded', 'false');
      const focusTarget = opener;
      opener = null;
      previousVehicle = null;
      const finish = () => {
        if (version !== dialogVersion) return;
        cancelDialogMotion();
        host.hidden = true;
        host.removeAttribute('inert');
        document.body.classList.remove('bm-vehicle-dialog-open');
        requestAnimationFrame(() => focusTarget?.focus?.());
      };
      dialogAnimations = dialogMotion(false);
      if (!dialogAnimations.length) { finish(); return; }
      dialogAnimations[0].finished.then(finish).catch(() => {});
    };

    const selectorTrigger = '[data-bm-product-vehicle-selector], [data-bm-compat-action], [data-bm-vehicle-change-link], [data-bm-vehicle-stock-link], a[href*="#voertuigselector"]';

    document.addEventListener('click', (event) => {
      const trigger = event.target.closest?.(selectorTrigger);
      if (!trigger || host.contains(trigger) || !api()?.isSelectorAction?.(trigger)) return;
      event.preventDefault();
      event.stopPropagation();
      open(trigger);
    });

    host.addEventListener('click', async (event) => {
      if (event.target.closest('[data-bm-vehicle-dialog-close]')) {
        close();
        return;
      }

      if (event.target.closest('[data-bm-dialog-reset]')) {
        reset();
        return;
      }

      const retry = event.target.closest('[data-bm-dialog-retry]');
      if (retry) {
        const stage = retry.dataset.bmDialogRetry;
        if (stage === 'brand') {
          try {
            await ensureBrands({ force: true });
            if (status) status.textContent = 'Kies eerst je automerk';
          } catch (error) {
            renderError(brandContainer, 'Kon de automerken niet laden.', 'brand');
          }
        } else {
          await loadStage(stage, { force: true });
        }
        return;
      }

      if (event.target.closest('[data-bm-dialog-back]')) {
        requestVersion++;
        if (state.stage === 'generation') {
          state.generation = null;
          setStage('model', true);
          if (status) status.textContent = `Kies je ${state.brand?.name || ''}-model`;
        } else if (state.stage === 'model') {
          state.model = null;
          state.generation = null;
          setStage('brand', true);
          if (status) status.textContent = 'Kies eerst je automerk';
        }
        return;
      }

      const choice = event.target.closest('.bm-choice[data-choice]');
      if (!choice || !host.contains(choice)) return;
      const type = choice.dataset.choice;
      qsa(host, `.bm-choice[data-choice="${type}"]`).forEach(item => {
        item.classList.toggle('is-selected', item === choice);
        item.setAttribute('aria-pressed', String(item === choice));
      });

      if (type === 'brand') {
        requestVersion++;
        state.brand = nodeData(choice);
        state.model = null;
        state.generation = null;
        setStage('model', true);
        if (status) status.textContent = `Modellen van ${state.brand.name} laden...`;
        const pending = loadStage('model');
        const version = requestVersion;
        const ok = await pending;
        if (version !== requestVersion) return;
        if (status) status.textContent = ok ? `Kies je ${state.brand.name}-model` : '';
        return;
      }

      if (type === 'model') {
        requestVersion++;
        state.model = nodeData(choice);
        state.generation = null;
        setStage('generation', true);
        if (status) status.textContent = `Uitvoeringen van ${state.model.name} laden...`;
        const pending = loadStage('generation');
        const version = requestVersion;
        const ok = await pending;
        if (version !== requestVersion) return;
        if (status) status.textContent = ok ? 'Kies je uitvoering' : '';
        return;
      }

      if (type === 'generation') {
        state.generation = nodeData(choice);
        if (!state.brand || !state.model || !state.generation.vehicleId) return;

        const vehicleId = state.generation.vehicleId;
        if (host.dataset.bmVehicleSelectionPending === vehicleId) return;
        const version = ++requestVersion;
        // Consume PDP/cart return before setActive emits bm:vehicle:change;
        // the existing garage listener otherwise consumes it synchronously.
        const returnUrl = api()?.consumeSelectionReturn?.() || '';
        selectionMade = Boolean(api()?.setActive?.({
          source: 'bridge-vehicle',
          brand:  {
            name: state.brand.name,
            logo: state.brand.logo || '',
          },
          model:  {
            name: state.model.name,
          },
          generation: null,
          newVehicle: {
            vehicleId: state.generation.vehicleId,
            brandRef: state.brand.ref,
            modelRef: state.model.ref,
            label: state.generation.name,
            brandLabel: state.brand.name,
            modelLabel: state.model.name,
            yearFrom: state.generation.yearFrom || null,
            yearTo: state.generation.yearTo || null,
            vehiclePageUrl: state.generation.url || null,
          },
          providerIds: {},
          selectedAt: Date.now(),
        }));

        if (status) status.textContent = selectionMade ? 'Auto geselecteerd' : 'Kon de auto niet opslaan';
        if (selectionMade) {
          previousVehicle = null;
          const destination = selectionDestination(state.generation.url, returnUrl);
          host.dataset.bmVehicleSelectionPending = vehicleId;
          let timer;
          try {
            if (typeof client()?.searchVehicleFitment === 'function') {
              if (status) status.textContent = 'Passende velgen voor jouw auto zoeken…';
              await Promise.race([
                client().searchVehicleFitment({ vehicleId }),
                new Promise((_, reject) => { timer = window.setTimeout(() => reject(Error('SEARCH_TIMEOUT')), 8000); }),
              ]);
            }
          } catch (_) {
            if (version === requestVersion && status) status.textContent = 'Passingscontrole niet afgerond. Resultaten openen…';
          } finally {
            window.clearTimeout(timer);
            if (host.dataset.bmVehicleSelectionPending === vehicleId) delete host.dataset.bmVehicleSelectionPending;
          }
          if (version !== requestVersion || api()?.getActive?.()?.newVehicle?.vehicleId !== vehicleId) return;
          if (destination && !isCurrentDestination(destination)) {
            window.location.assign(destination);
            return;
          }
          // Selecting the exact same page or returning to the current PDP
          // only updates garage/fitment state and closes the existing sheet.
          window.setTimeout(close, 90);
        }
      }
    });

    search?.addEventListener('input', () => {
      const query = normalizeLabel(search.value);
      const container = state.stage === 'brand'
        ? brandContainer
        : state.stage === 'model'
          ? modelContainer
          : typeContainer;
      let visible = 0;
      qsa(container, '.bm-choice').forEach((choice) => {
        const haystack = `${choice.dataset.name || ''} ${choice.dataset.yearFrom || ''} ${choice.dataset.yearTo || ''}`.toLocaleLowerCase('nl');
        choice.hidden = Boolean(query) && !haystack.includes(query);
        if (!choice.hidden) visible++;
      });
      let empty = qs(container, '[data-bm-dialog-filter-empty]');
      if (!empty) {
        empty = document.createElement('div');
        empty.className = 'bm-fitment__empty';
        empty.dataset.bmDialogFilterEmpty = '';
        container?.appendChild(empty);
      }
      empty.textContent = `Geen ${state.stage === 'brand' ? 'automerken' : state.stage === 'model' ? 'modellen' : 'uitvoeringen'} gevonden.`;
      empty.hidden = visible !== 0;
    });

    document.addEventListener('keydown', (event) => {
      if (host.hidden) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return;
      }
      if (event.key !== 'Tab' || !panel) return;
      const focusables = qsa(panel, 'button:not([disabled]):not([hidden]),input:not([disabled]),a[href],[tabindex]:not([tabindex="-1"])')
        .filter((element) => !element.hidden && element.offsetParent !== null);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    const generationProgress = qs(host, '[data-bm-dialog-progress="generation"]');
    if (generationProgress) {
      const text = [...generationProgress.childNodes].find((node) => node.nodeType === Node.TEXT_NODE);
      if (text) text.textContent = ' Uitvoering';
    }

    window.BandenmeestersVehicleSelector = { open, close };
    window.addEventListener('pageshow', (event) => {
      if (event.persisted && !host.hidden) close();
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
